import {getCountry, getLanguage} from '@aboutyou/config';
import {TransactionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {useLocale} from '@aboutyou/i18n';
import {
  TrackingTransactionItem,
  trackOrderSuccessPage,
} from '@aboutyou/legacy-tracking-sdk';
import {useCustomerSession} from '@aboutyou/user';
import {useTracking} from '../utils/utils';
import {waitUntilCustomerDataEvent} from '../utils/waitUntilCustomerDataEvent';

export const useTrackPurchase = () => {
  const {TrackingClient} = useTracking();
  const session = useCustomerSession();
  const locale = useLocale();

  return (tracker: TransactionTracker) => {
    const customerType = tracker.customer.isNewCustomer ? 'new' : 'existing';

    const trackingData = trackOrderSuccessPage({
      trackFitAnalytics: false,
      customer: {
        type: session ? customerType : 'guest',
        id: tracker.customer.customerId,
      },
      localeData: {
        countryCode: getCountry(locale),
        languageCode: getLanguage(locale),
      },
      recommendedSizes: [],
      voucher: {
        code: tracker.voucherCode || '',
      },
      transactions: {
        checkoutType: 'normal',
        orderId: tracker.orderId,
        total: {
          withoutTax: tracker.total.priceWithoutTax,
          withTax: tracker.total.price,
          fullPrice: tracker.total.fullPrice,
          saleReductionWithTax: tracker.total.saleReduction || 0,
          campaignReductionWithTax: tracker.total.campaignReduction || 0,
          couponReductionWithTax: tracker.total.couponReduction || 0,
          currency: tracker.total.currencyCode,
          deliveryCostWithoutTax: tracker.total.deliveryCostWithoutTax,
          voucherReductionWithTax: tracker.total.voucherReduction || 0,
        },

        items: tracker.items.map(
          // eslint-disable-next-line complexity
          item =>
            ({
              ...item,
              selectedQuantity: item.quantity,
              product: {
                id: item.productTracker.productId,
                name: item.productTracker.productName,
                url: item.productTracker.linkTarget || '',
                brand: {
                  id: item.brandTracker.id,
                  name: item.brandTracker.name,
                },
                colors: [],
                minPrice: {
                  withoutTax: item.totalPrice.priceWithoutTax,
                  withTax: item.totalPrice.price,
                  fullPrice: item.totalPrice.fullPrice,
                  saleReductionWithTax: item.totalPrice.saleReduction || 0,
                  campaignReductionWithTax:
                    item.totalPrice.campaignReduction || 0,
                  couponReductionWithTax: item.totalPrice.couponReduction || 0,
                  coinsReductionWithTax: item.totalPrice.coinsReduction || 0,
                  voucherReductionWithTax:
                    item.totalPrice.voucherReduction || 0,
                  currency: item.totalPrice.currencyCode,
                },
                isSoldOut: item.productTracker.isSoldOut,
                isSponsored: item.productTracker.isSponsored,
                sponsoredType: item.productTracker.sponsoredType,
                badges: item.additionalData?.itemBadge?.split(',') || [],
                // not needed anylonger
                masterKey: '',
              },
              variant: {
                price: {
                  withoutTax: item.unitPrice.priceWithoutTax,
                  withTax: item.unitPrice.price,
                  fullPrice: item.unitPrice.fullPrice,
                  saleReductionWithTax: item.unitPrice.saleReduction || 0,
                  campaignReductionWithTax:
                    item.unitPrice.campaignReduction || 0,
                  couponReductionWithTax: item.unitPrice.couponReduction || 0,
                  coinsReductionWithTax: item.unitPrice.coinsReduction || 0,
                  voucherReductionWithTax: item.unitPrice.voucherReduction || 0,
                  currency: item.unitPrice.currencyCode,
                },
                id: item.variant.id,
                sizeLabel: item.variant.sizeLabel,
                supplierId: item.variant.supplierId,
              },
              unitPrice: {
                withoutTax: item.unitPrice.priceWithoutTax,
                withTax: item.unitPrice.price,
                fullPrice: item.unitPrice.fullPrice,
                saleReductionWithTax: item.unitPrice.saleReduction || 0,
                campaignReductionWithTax: item.unitPrice.campaignReduction || 0,
                couponReductionWithTax: item.unitPrice.couponReduction || 0,
                coinsReductionWithTax: item.unitPrice.coinsReduction || 0,
                voucherReductionWithTax: item.unitPrice.voucherReduction || 0,
                currency: item.unitPrice.currencyCode,
              },
              totalPrice: {
                withoutTax: item.totalPrice.priceWithoutTax,
                withTax: item.totalPrice.price,
                fullPrice: item.totalPrice.fullPrice,
                saleReductionWithTax: item.totalPrice.saleReduction || 0,
                campaignReductionWithTax:
                  item.totalPrice.campaignReduction || 0,
                couponReductionWithTax: item.totalPrice.couponReduction || 0,
                coinsReductionWithTax: item.totalPrice.coinsReduction || 0,
                voucherReductionWithTax: item.totalPrice.voucherReduction || 0,
                currency: item.totalPrice.currencyCode,
              },
            } satisfies TrackingTransactionItem),
        ),
        payment: tracker.payments.map(payment => ({
          name: payment.type,
          type: payment.type,
          totalWithTax: payment.totalWithTax,
        })),
      },
      carriers: tracker.carriers,
    });

    waitUntilCustomerDataEvent(() => {
      TrackingClient.track(trackingData);
    });
  };
};
